import { useState, useEffect, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api, LOGIN_NOTICE_KEY, PORTAL_ACCESS_ENDED, SESSION_ENDED } from '../services/api';
import { isPortalIdentity } from '../components/permissions/screenAccess';
import { AlertCircle, Eye, EyeOff, Info, Loader2 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useBrand } from '../providers/BrandProvider';
import { isLoginUpgradeNoticeEnabled } from '../utils/featureFlags';
import { isClaudeConnectReturn } from '../utils/claudeConnect';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

/** Google's "G" mark. Its four brand colours live in the static SVG (third-party mark, not our tokens). */
function GoogleIcon({ className }: { className?: string }) {
  return <img src="/icons/google-g.svg" width={20} height={20} alt="" aria-hidden="true" className={className} />;
}

export default function LoginPage() {
  const { t } = useTranslation();
  const showUpgradeNotice = isLoginUpgradeNoticeEnabled();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const brand = useBrand();
  const [error, setError] = useState('');
  // Q-DQ-005: a soft (non-error) note after a forced sign-out.
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // D-142 / QA-5 #2: signed out because the portal access ended.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(LOGIN_NOTICE_KEY) === PORTAL_ACCESS_ENDED) {
        sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        setError(t('auth.portalAccessEnded'));
      } else if (sessionStorage.getItem(LOGIN_NOTICE_KEY) === SESSION_ENDED) {
        sessionStorage.removeItem(LOGIN_NOTICE_KEY);
        setNotice(t('auth.sessionEnded'));
      }
    } catch { /* storage off */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // FIX-CLAUDE-B (D-314 §3): Google came back from "connect Claude to OTE" (the server's state
    // starts with "mcp."): the server finishes that sign-in, not the website login.
    if (isClaudeConnectReturn(searchParams.get('state'))) {
      window.location.replace(`/mcp/oauth/google/callback${window.location.search}`);
      return;
    }
    const code = searchParams.get('code');
    if (code) {
      searchParams.delete('code');
      searchParams.delete('scope');
      searchParams.delete('authuser');
      searchParams.delete('prompt');
      searchParams.delete('iss');
      searchParams.delete('hd');
      setSearchParams(searchParams, { replace: true });
      handleGoogleCallback(code);
    }
    // Mount-only on purpose: the OAuth code in the URL is consumed once and then removed;
    // re-running on searchParams / handler identity would replay the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.login({ email, password });

      if (response.success && response.data) {
        const { token, user, organization } = response.data;
        setAuth(token, user, organization);
        navigate(isPortalIdentity(user) ? '/portal' : '/');
      } else {
        setError(response.error?.message || t('auth.loginError'));
      }
    } catch (err: any) {
      // FIX-HN2 (1.1-7): a temporary account lock carries its own text ("נסו שוב בעוד X דקות").
      if (err?.error?.error_code === 'ACCOUNT_LOCKED' && err?.error?.message) setError(err.error.message);
      // 429 from the API rate limiter (plain-text body): say so, never "wrong password".
      else if (err?.response?.status === 429) setError(t('auth.tooManyAttempts'));
      else if (err?.error?.error_code === PORTAL_ACCESS_ENDED) setError(t('auth.portalAccessEnded'));
      else setError(err?.error?.message || t('auth.loginErrorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError(t('auth.googleNotConfigured'));
      return;
    }

    const redirectUri = `${window.location.origin}/login`;
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'email profile',
      access_type: 'offline',
      prompt: 'select_account',
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const handleGoogleCallback = async (code: string) => {
    setGoogleLoading(true);
    setError('');

    try {
      const redirectUri = `${window.location.origin}/login`;
      const response = await api.loginWithGoogle({ code, redirectUri });

      if (response.success && response.data) {
        const { token, user, organization } = response.data;
        setAuth(token, user, organization);
        navigate(isPortalIdentity(user) ? '/portal' : '/');
      } else {
        setError(response.error?.message || t('auth.googleLoginFailed'));
      }
    } catch (err: any) {
      if (err?.response?.status === 429) setError(t('auth.tooManyAttempts'));
      else if (err?.error?.error_code === PORTAL_ACCESS_ENDED) setError(t('auth.portalAccessEnded'));
      else setError(err?.error?.message || t('auth.googleLoginFailed'));
    } finally {
      setGoogleLoading(false);
    }
  };

  const isLoading = loading || googleLoading;

  const upgradeNotice = showUpgradeNotice && (
    <div role="status" className="flex gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 mb-6">
      <Info size={18} className="text-warning-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="text-sm">
        <p className="font-medium text-warning-900">{t('auth.upgradeTitle')}</p>
        <p className="text-surface-700 mt-0.5">{t('auth.upgradeMessage')}</p>
        <p className="text-surface-600 mt-1 text-xs leading-relaxed">{t('auth.upgradeDetails')}</p>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen flex flex-col lg:flex-row bg-surface">
      {/* Language before sign-in (PRD §6.11): endonym switch, top end corner. */}
      <div className="absolute top-4 end-4 z-sticky">
        <LanguageSwitcher />
      </div>

      {/* Brand panel (desktop, start side): white, pattern A built from the logo itself and
          the official lockup in its clear zone (BK-3, DESIGN_SYSTEM.md §9.3). Decorative
          background, no text; the form side stays plain. */}
      <div
        className="login-brand hidden lg:block lg:w-1/2 lg:h-screen lg:sticky lg:top-0 border-e border-border"
        data-pattern={brand.loginPatternUrl ? 'true' : undefined}
        data-pattern-dark={brand.loginPatternDarkUrl ? 'true' : undefined}
        // Light and dark patterns as variables; index.css picks one by appearance (A10).
        style={{
          ...(brand.loginPatternUrl ? { '--login-pattern': `url("${brand.loginPatternUrl}")` } : {}),
          ...(brand.loginPatternDarkUrl ? { '--login-pattern-dark': `url("${brand.loginPatternDarkUrl}")` } : {}),
        } as CSSProperties}
      >
        <div className="login-brand-lockup">
          <BrandLogo variant="lockup" fill />
        </div>
      </div>

      {/* Mobile / tablet: the lockup alone above the form (≥ 96 px), no navy band. */}
      <div className="lg:hidden flex justify-center px-6 pt-10">
        <BrandLogo variant="lockup" />
      </div>

      {/* Form */}
      <main className="flex-1 flex flex-col items-center px-6">
        <div className="w-full max-w-sm pt-8 pb-10 lg:my-auto lg:py-12">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-surface-900 tracking-tight">
              {t('auth.loginTitle')}
            </h1>
            <p className="text-sm text-surface-600 mt-1.5">{brand.productName} · {t('auth.loginSubtitle')}</p>
          </div>

          {upgradeNotice}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="label">
                {t('common.email')}
              </label>
              <input
                id="email"
                type="email"
                dir="ltr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`input py-2.5 text-start ${error ? 'input-error' : ''}`}
                placeholder={t('auth.emailPlaceholder', 'your@email.com')}
                required
                autoComplete="email"
                aria-invalid={!!error}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                {t('common.password')}
              </label>
              <div className="relative" dir="ltr">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`input py-2.5 pe-10 text-start ${error ? 'input-error' : ''}`}
                  required
                  autoComplete="current-password"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('uxFinance.login.hidePassword') : t('uxFinance.login.showPassword')}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 end-0 flex items-center px-3 rounded-e-lg text-surface-500 hover:text-surface-900"
                >
                  {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                </button>
              </div>
            </div>

            {notice && !error && (
              <div role="status" className="rounded-lg bg-surface-100 px-3.5 py-3 text-sm text-surface-800" data-testid="login-session-ended">{notice}</div>
            )}

            {error && (
              <div id="login-error" role="alert" className="flex items-start gap-2 rounded-lg bg-danger-50 px-3.5 py-3 text-sm text-danger-800">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-danger-600" aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <div className="text-end">
              <Link to="/forgot-password" className="text-sm text-primary-700 hover:text-primary-900">
                {t('resetPassword.forgotLink')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              aria-busy={loading}
              className="w-full btn btn-primary btn-lg"
            >
              {loading && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
              <span>{loading ? t('common.loading') : t('auth.login')}</span>
            </button>
          </form>

          {/* Google Login Button — below the form */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="flex items-center gap-3 my-6" aria-hidden="true">
                <div className="h-px flex-1 bg-surface-200" />
                <span className="text-xs text-surface-500">{t('auth.or')}</span>
                <div className="h-px flex-1 bg-surface-200" />
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full btn btn-secondary btn-lg"
              >
                {googleLoading ? (
                  <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                ) : (
                  <GoogleIcon />
                )}
                <span>{googleLoading ? t('common.loading') : t('auth.loginWithGoogle')}</span>
              </button>
            </>
          )}

        </div>
        <footer className="w-full max-w-sm mt-auto pb-6 text-center text-xs text-surface-500">
          <bdi dir="ltr">{brand.copyrightText}</bdi>
        </footer>
      </main>
    </div>
  );
}
